import { confirmDialog } from "../components/confirmHost.jsx";
import React, { useMemo, useState } from "react";
import { Users, TrendingUp, ChevronDown, ChevronUp, ChevronRight, RefreshCw, Award, Activity, Calendar, Package, AlertCircle, Target, DollarSign, Percent, ArrowUp, ArrowDown, Clock, Pencil, X as XIcon, Briefcase } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import {
  Stranka, RozbalovaciVyber, Zalozky, Dlazdice, ErrorNote, Hlaska, EmptyState, SkeletonRows,
} from "../components/ui.jsx";
import { KartaPrehledu } from "../components/PrehledDlazdice.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { fmt } from "../lib/format.js";
import InfoTip from "../components/InfoTip.jsx";
import ClientLink from "../components/ClientLink.jsx";
import { LOKALITY } from "../lib/lokality.js";

// Slug trenéra z display_name — musí odpovídat backend migrate.js logice (PG trainers.id).
function trenerIdFromDisplayName(jmeno) {
  return String(jmeno || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// Spočítá ordinální měsíc spolupráce trenéra od data začátku (zacatek YYYY-MM-DD).
// 1-indexed: první den = 1. měsíc, po 30 dnech = 2. měsíc atd.
// Vrací null pokud chybí nebo neplatný datum.
function ordinalMesicSpoluprace(zacatekIso) {
  if (!zacatekIso) return null;
  const from = new Date(zacatekIso + "T00:00:00");
  if (isNaN(from.getTime())) return null;
  const now = new Date();
  const yearDiff = now.getFullYear() - from.getFullYear();
  const monthDiff = now.getMonth() - from.getMonth();
  let months = yearDiff * 12 + monthDiff;
  if (now.getDate() < from.getDate()) months--;
  return Math.max(0, months) + 1;
}

// Helper — přeložit Google Drive URL na přímý image link.
// Podporuje: https://drive.google.com/file/d/FILE_ID/view, https://drive.google.com/open?id=FILE_ID,
// https://drive.google.com/uc?id=FILE_ID, přímé FILE_ID. Jinak vrací URL beze změny.
// Pridan suffix =s100-c (square 100px crop) — Google Drive vraci predcachovany thumbnail
// rychleji a stabilneji nez plnou velikost, navic browser cache hit ratio vyssi.
function rewriteGoogleDrive(url) {
  if (!url) return "";
  const s = String(url).trim();
  if (!s) return "";
  // file/d/FILE_ID/...
  let m = s.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (m) return `https://lh3.googleusercontent.com/d/${m[1]}=s100-c`;
  // ?id=FILE_ID
  m = s.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m) return `https://lh3.googleusercontent.com/d/${m[1]}=s100-c`;
  // raw file ID (dlouhý alfanumerický bez lomítek)
  if (/^[a-zA-Z0-9_-]{20,}$/.test(s)) return `https://lh3.googleusercontent.com/d/${s}=s100-c`;
  return s;
}

export default function Trainers() {
  const currentYear = new Date().getFullYear();
  const [year] = useState(currentYear);
  // 13.9.2026 (fáze 4f, rozhodnutí Č2): lokalita se vybírá jedna, prázdná hodnota
  // znamená všechny. Dřív to byl multi-select čipů; přepínač je teď stejný jako
  // ostatní výběry v aplikaci, takže s přibývajícími lokalitami se seznam jen
  // prodlouží.
  const [lokalita, setLokalita] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | all
  const { blurred } = useDemoMode();
  const [expandedId, setExpandedId] = useState(null);
  // Email editace a Add trainer modal odstraněny 22.5.2026 — přesunuto do HR / Lidé.
  // Modal pro upload fotky. null | { trenerId, displayName, currentFoto }
  const [photoEditFor, setPhotoEditFor] = useState(null);

  // Server vrátí všechny trenéry, filtr lokality řeší client-side (okamžité přepnutí, žádný refetch)
  const { data, loading, error, refreshing, refetch } = useApiData(
    `/api/trainers?year=${year}`
  );

  // Capacity data ze stejného zdroje jako dashboard — pro sloupec obsazenosti v tabulce
  const { data: overviewData } = useApiData("/api/data-overview");
  const capacityBreakdown = overviewData?.capacity?.trainerBreakdown || [];
  // Mapa trenér jméno (normalizované) → capacity data
  const capByName = useMemo(() => {
    const map = new Map();
    for (const t of capacityBreakdown) {
      const norm = String(t.name || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
      map.set(norm, t);
    }
    return map;
  }, [capacityBreakdown]);

  // Merge: enrichovat každého trenéra o capacity
  const trainers = useMemo(() => {
    const raw = data?.trainers || [];
    return raw.map((t) => {
      const norm = String(t.jmeno || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
      const cap = capByName.get(norm);
      return cap ? { ...t, capacityHours: cap.capacityHours, usedHoursPerWeek: cap.usedHoursPerWeek, utilizationPct: cap.utilizationPct } : t;
    });
  }, [data, capByName]);
  const summary = data?.summary || { totalActiveTrainers: 0, totalActiveClients: 0, totalMrr: 0, totalLifetimeRev: 0 };
  const availableLocations = data?.meta?.availableLocations || [];
  const leadyHasTrener = data?.meta?.leadyHasTrener || false;
  const lokalityHasMaxColumn = data?.meta?.lokalityHasMaxColumn || false;
  const lokalityCapacity = data?.meta?.lokalityCapacity || {}; // { "Praha - Kbely - VT GYM": 3, ... }

  // Fuzzy mapa lokalita → kapacita. Normalizace (lowercase, bez diakritiky) + fallback na poslední segment.
  const capacityByNorm = useMemo(() => {
    const map = new Map();
    const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
    for (const [loc, cap] of Object.entries(lokalityCapacity)) {
      const n = norm(loc);
      map.set(n, cap);
      // Fallback — poslední část po pomlčce (např. "VT GYM", "Sportovní Hala")
      const parts = n.split(/\s*-\s*/);
      const last = parts[parts.length - 1]?.trim();
      if (last && last !== n && !map.has(last)) map.set(last, cap);
    }
    return map;
  }, [lokalityCapacity]);

  const getLocationCapacity = (loc) => {
    const n = String(loc || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
    if (capacityByNorm.has(n)) return capacityByNorm.get(n);
    // Fuzzy: oboustranný substring match
    for (const [key, val] of capacityByNorm) {
      if (key.includes(n) || n.includes(key)) return val;
    }
    return 0;
  };

  const totalActiveAllLocations = useMemo(
    () => trainers.filter((t) => t.status === "Aktivní").length,
    [trainers]
  );

  const visibleTrainers = useMemo(() => {
    let list = trainers;
    if (statusFilter === "active") list = list.filter((t) => t.status === "Aktivní");
    if (lokalita) list = list.filter((t) => t.lokalita === lokalita);
    return list;
  }, [trainers, statusFilter, lokalita]);

  // Čitatel a jmenovatel pro dlaždici "Aktivní trenéři X z Y": bez výběru celá
  // firma, s vybranou lokalitou jen ona.
  const effectiveActiveTrainers = useMemo(() => {
    if (!lokalita) return summary.totalActiveTrainers || totalActiveAllLocations;
    return trainers.filter((t) => t.status === "Aktivní" && t.lokalita === lokalita).length;
  }, [trainers, lokalita, summary, totalActiveAllLocations]);

  const effectiveMaxTrainers = useMemo(() => {
    if (!lokalita) return summary.maxTrainers || null;
    const cap = getLocationCapacity(lokalita);
    return cap > 0 ? cap : null;
  }, [lokalita, summary, capacityByNorm]);

  const polozkyLokalit = useMemo(
    () => [{ key: "", label: "Všechny" }, ...availableLocations.map((loc) => ({ key: loc, label: loc }))],
    [availableLocations]
  );

  return (
    <Stranka className={blurred ? "demo-blur" : ""}>
      <GradientHeader
        title="Trenéři"
        icon={Users}
        loading={refreshing}
        napoveda="Všichni trenéři s metrikami: počet aktivních klientů, MRR z karet klientů (pravidelní klienti, bez permic), revenue z permic (počet absolvovaných tréninků za 30 dní krát cena za trénink z permice; cena je Částka za měsíc dělená Tréninky z fakturace, fallback 40 % z Částky, když Tréninky chybí), lifetime revenue z FAPI, skutečná kapacita z týdenního rozvrhu trenéra, win rate trialů z leadů (60 dní i celá historie), Ø délka spolupráce podle počtu faktur. Klepnutím na kartu se otevře detail. Nové osoby a úpravy e-mailů se řeší v HR, Lidé."
        akce={[
          { klic: "hr", ikona: Briefcase, popis: "Spravovat osoby v HR / Lidé", onClick: () => { window.location.href = "/hr/lide"; } },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit data", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* Filtry: lokalita jako výběr se štítkem, pod ním kdo se má ukázat. */}
      <div className="space-y-2">
        <RozbalovaciVyber
          stitek="Lokalita"
          items={polozkyLokalit}
          value={lokalita}
          onChange={setLokalita}
          aria-label="Filtr lokality"
        />
        <Zalozky
          stitek="Kdo"
          aria-label="Kdo se má ukázat"
          items={[{ key: "active", label: "Aktivní" }, { key: "all", label: "Všichni" }]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {/* Čísla za celou firmu, nebo za vybranou lokalitu u počtu trenérů. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Dlazdice
          popisek="Aktivní trenéři"
          hodnota={effectiveMaxTrainers ? `${effectiveActiveTrainers} z ${effectiveMaxTrainers}` : effectiveActiveTrainers}
          ton="znacka"
          doplnky={
            <span className="text-xs text-slate-500">
              {effectiveMaxTrainers
                ? `využití ${Math.round((effectiveActiveTrainers / effectiveMaxTrainers) * 100)} % kapacity`
                : "max. počet trenérů v lokalitách není vyplněný"}
            </span>
          }
        />
        <Dlazdice
          popisek="Aktivních klientů"
          hodnota={fmt(summary.totalActiveClients)}
          ton="info"
        />
        <Dlazdice
          popisek="MRR celkem"
          hodnota={`${fmt(summary.totalMrr)} Kč`}
          ton="ok"
          doplnky={
            <span className="text-xs text-slate-500">
              {summary.totalPermicePotential > 0
                ? `+ ${fmt(summary.totalPermicePotential)} Kč z permic, ${summary.totalRegularClients} pravidelných`
                : `${summary.totalRegularClients} pravidelných klientů`}
            </span>
          }
        />
        <Dlazdice
          popisek="Lifetime revenue"
          hodnota={`${fmt(summary.totalLifetimeRev)} Kč`}
          ton="pozor"
          doplnky={<span className="text-xs text-slate-500">od 1. 1. 2025</span>}
        />
      </div>

      {/* Náklady na trenéry, sazby krát tréninky podle smlouvy. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Dlazdice
          popisek="Náklady tento měsíc"
          hodnota={`${fmt(summary.totalWagesMesic || 0)} Kč`}
          ton="problem"
          doplnky={<span className="text-xs text-slate-500">{summary.totalTreninkyMesic || 0} tréninků zatím</span>}
        />
        <Dlazdice
          popisek={wagesYTDLabel(summary)}
          hodnota={`${fmt(summary.totalWagesYTDClosed || 0)} Kč`}
          ton="problem"
          doplnky={
            <span className="text-xs text-slate-500">
              {summary.closedMonthsCount > 0
                ? `${summary.totalTreninkyYTDClosed || 0} tréninků za ${summary.closedMonthsCount} ${summary.closedMonthsCount === 1 ? "uzavřený měsíc" : summary.closedMonthsCount < 5 ? "uzavřené měsíce" : "uzavřených měsíců"}`
                : "zatím žádný uzavřený měsíc letos"}
            </span>
          }
        />
        <Dlazdice
          popisek="Ø sazba"
          hodnota={summary.totalTreninkyMesic > 0 ? `${fmt(Math.round((summary.totalWagesMesic || 0) / summary.totalTreninkyMesic))} Kč` : "—"}
          ton="znacka"
          doplnky={<span className="text-xs text-slate-500">náklad na jeden trénink</span>}
        />
        <Dlazdice
          popisek="MRR ku nákladům"
          hodnota={summary.totalWagesMesic > 0 ? `${(summary.totalMrr / summary.totalWagesMesic).toFixed(2)}×` : "—"}
          ton="ok"
          doplnky={
            <span className="text-xs text-slate-500">
              {summary.totalWagesMesic > 0
                ? `${fmt(summary.totalMrr - summary.totalWagesMesic)} Kč hrubý profit`
                : "MRR proti nákladům na trenéry"}
            </span>
          }
        />
      </div>

      {/* Výkonnost za 60 dní */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Dlazdice
          popisek="Trialy za 60 dní"
          hodnota={fmt(summary.trials60 || 0)}
          ton="znacka"
          doplnky={<span className="text-xs text-slate-500">realizované zkušební tréninky</span>}
        />
        <Dlazdice
          popisek="Výhry za 60 dní"
          hodnota={fmt(summary.deals60 || 0)}
          ton="ok"
          doplnky={<span className="text-xs text-slate-500">klient uzavřen</span>}
        />
        <Dlazdice
          popisek="Prohry za 60 dní"
          hodnota={fmt(summary.losses60 || 0)}
          ton="problem"
          doplnky={<span className="text-xs text-slate-500">klient neuzavřen</span>}
        />
        <Dlazdice
          popisek="Win rate za 60 dní"
          hodnota={summary.winRate60 != null ? `${summary.winRate60} %` : "—"}
          ton="info"
          hodnotaTon={summary.winRate60 >= 50 ? "ok" : summary.winRate60 >= 30 ? "pozor" : "problem"}
          doplnky={
            <span className="text-xs text-slate-500">
              {summary.unassigned60?.trials > 0 ? `+ ${summary.unassigned60.trials} nepřiřazených` : "výhry z rozhodnutých"}
            </span>
          }
        />
      </div>

      {error && <ErrorNote onRetry={refetch}>Nepodařilo se načíst trenéry: {error}</ErrorNote>}

      {loading && <SkeletonRows rows={5} />}

      {!loading && visibleTrainers.length === 0 && (
        <KartaPrehledu>
          <EmptyState
            icon={Users}
            title="Žádní trenéři"
            hint="Zkus přepnout lokalitu nebo ukázat všechny, ne jen aktivní."
          />
        </KartaPrehledu>
      )}

      {!loading && visibleTrainers.length > 0 && (
        <div className="space-y-3">
          {visibleTrainers.map((t) => (
            <TrainerRow
              key={t.jmeno}
              trainer={t}
              expanded={expandedId === t.jmeno}
              onToggle={() => setExpandedId(expandedId === t.jmeno ? null : t.jmeno)}
              onEditPhoto={() => setPhotoEditFor({
                trenerId: t.id || trenerIdFromDisplayName(t.jmeno),
                displayName: t.jmeno,
                currentFoto: t.foto || "",
              })}
            />
          ))}
        </div>
      )}

      {!loading && data?.meta?.masterTrainerCount === 0 && (
        <Hlaska ton="pozor" title="Seznam trenérů je prázdný">
          V databázi nejsou žádní trenéři. Přidej je v HR, Lidé (Přidat osobu).
        </Hlaska>
      )}

      {!loading && !lokalityHasMaxColumn && (
        <Hlaska ton="pozor" title="Maximální počet trenérů se nenačetl">
          Nepodařilo se načíst maximální počet trenérů v lokalitách, proto chybí „X z Y".
          Zkus stránku obnovit.
        </Hlaska>
      )}

      {!loading && !leadyHasTrener && summary.unassigned60?.trials > 0 && (
        <Hlaska ton="pozor" title="U leadů chybí trenér">
          Žádný lead nemá vyplněného kouče ani trenéra zkušebního tréninku. Teď je nepřiřazeno {summary.unassigned60.trials} trialů
          ({summary.unassigned60.losses} proher, {summary.unassigned60.deals} výher, {summary.unassigned60.pending} čekajících).
        </Hlaska>
      )}

      {/* ChangeTrainerEmailModal a AddTrainerModal odstraněny 22.5.2026 — email a přidání
          osoby se řeší v HR / Lidé. Komponenty zůstávají v souboru jako dead code zatím,
          aby šly snadno vrátit při potřebě. */}

      {photoEditFor && (
        <TrainerPhotoModal
          trenerId={photoEditFor.trenerId}
          displayName={photoEditFor.displayName}
          currentFoto={photoEditFor.currentFoto}
          onClose={() => setPhotoEditFor(null)}
          onSaved={() => { setPhotoEditFor(null); refetch(); }}
        />
      )}
    </Stranka>
  );
}

// Modal pro upload fotky trenéra do Netlify Blobs přes /api/trainer-photo POST.
// Akceptuje image/* (kontrola na frontu), max 5 MB (kontrola na backu).
// Optimalizuje na 400×400 přes canvas resize před uploadem (rychlejší + menší).
function TrainerPhotoModal({ trenerId, displayName, currentFoto, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentFoto || "");
  const [phase, setPhase] = useState("input"); // "input" | "uploading" | "error" | "done"
  const [errorMsg, setErrorMsg] = useState("");

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setErrorMsg("Vyber prosím obrázek (jpg, png, webp).");
      setPhase("error");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErrorMsg(`Soubor je velký (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      setPhase("error");
      return;
    }
    setFile(f);
    setErrorMsg("");
    setPhase("input");
    // Preview
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(String(reader.result));
    reader.readAsDataURL(f);
  };

  // Resize obrázku na max 400×400 přes canvas → menší upload + rychlejší render.
  const resizeImage = (dataUrl, maxSize = 400) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      if (width > height) {
        if (width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
      } else {
        if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });

  const submit = async () => {
    if (!file) return;
    setPhase("uploading");
    setErrorMsg("");
    try {
      const reader = new FileReader();
      const dataUrlPromise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const rawDataUrl = await dataUrlPromise;
      const resized = await resizeImage(rawDataUrl, 400);

      const r = await apiFetch("/api/trainer-photo", {
        method: "POST",
        body: JSON.stringify({ trenerId, dataUrl: resized }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        setErrorMsg(j.error || `HTTP ${r.status}`);
        setPhase("error");
        return;
      }
      setPhase("done");
      setTimeout(() => onSaved(), 600);
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  const handleDelete = async () => {
    if (!await confirmDialog(`Smazat fotku trenéra ${displayName}?`)) return;
    setPhase("uploading");
    try {
      const r = await apiFetch(`/api/trainer-photo?id=${encodeURIComponent(trenerId)}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        setErrorMsg(j.error || "Smazání selhalo");
        setPhase("error");
        return;
      }
      onSaved();
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Fotka trenéra, {displayName}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded min-h-[44px] sm:min-h-0"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Preview */}
          <div className="flex justify-center">
            {previewUrl ? (
              <img src={previewUrl.startsWith("data:") ? previewUrl : rewriteGoogleDrive(previewUrl)} alt={displayName} className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-brand-500 to-plum-600 flex items-center justify-center text-white font-bold text-3xl">
                {displayName.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
              </div>
            )}
          </div>

          {phase === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-900">
              <b>Chyba:</b> {errorMsg}
            </div>
          )}
          {phase === "done" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900">
              Fotka nahrána a uložena.
            </div>
          )}

          <label className="block">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nahrát novou fotku</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="mt-1 w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:bg-brand-600 file:text-white hover:file:bg-brand-700 file:cursor-pointer"
            />
            <span className="text-xs text-slate-500">Akceptuje jpg/png/webp, max 5 MB. Automaticky se zmenší na 400×400 px.</span>
          </label>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-between gap-2">
          {currentFoto && phase !== "uploading" && phase !== "done" && (
            <button
              onClick={handleDelete}
              className="px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 rounded-xl min-h-[44px] sm:min-h-0"
            >
              Smazat fotku
            </button>
          )}
          <div className="flex-1" />
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zrušit</button>
            {phase === "uploading" ? (
              <div className="px-3 py-1.5 text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Nahrávám…</div>
            ) : (
              <button onClick={submit} disabled={!file} className="btn btn-primary">
                Nahrát
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal pro vytvoření nového trenéra — POST /api/admin-create-trainer.
// Pole: jméno (povinné), email, telefon, role, lokalita, sazba, kapacita,
// začátek spolupráce, status (Aktivní default).
function AddTrainerModal({ onClose, onCreated }) {
  const today = new Date().toISOString().substring(0, 10);
  const [form, setForm] = useState({
    jmeno: "", email: "", telefon: "", role: "trener", lokalita: "",
    kapacita: "", zacatek: today, status: "Aktivní",
  });
  const [phase, setPhase] = useState("input"); // "input" | "saving" | "done" | "error"
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit = form.jmeno.trim().length > 0 && (!form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()));

  const submit = async () => {
    if (!canSubmit) return;
    setPhase("saving");
    setErrorMsg("");
    try {
      const r = await apiFetch("/api/admin-create-trainer", {
        method: "POST",
        body: JSON.stringify({
          jmeno: form.jmeno.trim(),
          email: form.email.trim() || null,
          telefon: form.telefon.trim() || null,
          role: form.role.trim() || null,
          lokalita: form.lokalita.trim() || null,
          kapacita: form.kapacita || null,
          zacatek: form.zacatek || null,
          status: form.status,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) { setErrorMsg(j.error || `HTTP ${r.status}`); setPhase("error"); return; }
      setResult(j);
      setPhase("done");
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Přidat nového trenéra</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded min-h-[44px] sm:min-h-0"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {phase === "done" && result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900">
              <div className="font-bold">Trenér vytvořen: {result.displayName}</div>
              <div className="text-xs mt-1">
                PG trainers: ✓
              </div>
              <div className="text-xs mt-2 text-slate-600">
                Slug ID v PG: <code className="bg-white px-1.5 py-0.5 rounded">{result.trenerId}</code>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-900">
              <b>Chyba:</b> {errorMsg}
            </div>
          )}

          {phase !== "done" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block col-span-2">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Jméno *</span>
                  <input
                    type="text" value={form.jmeno} onChange={setField("jmeno")}
                    placeholder="Petr Janata"
                    className="field mt-1 w-full"
                    autoFocus
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</span>
                  <input
                    type="email" value={form.email} onChange={setField("email")}
                    placeholder="petr.janata.fw40@gmail.com"
                    className="field mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Telefon</span>
                  <input
                    type="tel" value={form.telefon} onChange={setField("telefon")}
                    placeholder="+420 777 123 456"
                    className="field mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Role</span>
                  <select value={form.role} onChange={setField("role")} className="field mt-1 w-full">
                    <option value="trener">trener</option>
                    <option value="head-trener">head-trener</option>
                    <option value="csm">csm</option>
                    <option value="manager">manager</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Lokalita</span>
                  <select value={form.lokalita} onChange={setField("lokalita")} className="field mt-1 w-full">
                    <option value="">— vyber —</option>
                    {/* 4.9.2026: seznam je v src/lib/lokality.js. Dřív tady bylo
                        natvrdo „VT GYM", zatímco HR modal ukládal „VT GYM - Kbely",
                        takže jedno místo vypadalo jako dvě různé lokality. */}
                    {LOKALITY.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Kapacita (h/měs)</span>
                  <input
                    type="number" min="0" value={form.kapacita} onChange={setField("kapacita")}
                    placeholder="napr. 80"
                    className="field mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Začátek spolupráce</span>
                  <input
                    type="date" value={form.zacatek} onChange={setField("zacatek")}
                    className="field mt-1 w-full"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</span>
                  <select value={form.status} onChange={setField("status")} className="field mt-1 w-full">
                    <option value="Aktivní">Aktivní</option>
                    <option value="Neaktivní">Neaktivní</option>
                  </select>
                </label>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <b>Po vytvoření:</b> trenér je v databázi. Pro plný onboarding ještě:
                <ul className="list-disc ml-4 mt-1">
                  <li>Reservio: přidat trenéra do Personál + napojit Google Calendar</li>
                  <li>Pokud má login do admin appce, přidat ho v Nastavení mezi uživatele (email + role)</li>
                  <li>Fotka: nahrát přes úpravu fotky u trenéra v tomto přehledu</li>
                </ul>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          {phase === "input" && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zrušit</button>
              <button onClick={submit} disabled={!canSubmit} className="btn btn-primary">Vytvořit trenéra</button>
            </>
          )}
          {phase === "saving" && (
            <div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Ukládám…</div>
          )}
          {phase === "done" && (
            <button onClick={onCreated} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-700 min-h-[44px] sm:min-h-0">Zavřít a obnovit</button>
          )}
          {phase === "error" && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zavřít</button>
              <button onClick={() => setPhase("input")} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-700">Zkusit znovu</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal pro změnu emailu trenéra — volá POST /api/admin-update-trainer-email.
// Krok 1: dry-run preview (co se změní v databázi).
// Krok 2: confirm → live commit (trainers + uzivatele v jedné transakci, jen databáze).
function ChangeTrainerEmailModal({ trenerId, displayName, currentEmail, onClose, onSaved }) {
  const [newEmail, setNewEmail] = useState("");
  const [phase, setPhase] = useState("input"); // "input" | "preview" | "saving" | "done" | "error"
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim());

  const doDryRun = async () => {
    if (!isValid) return;
    setPhase("saving");
    setErrorMsg("");
    try {
      const r = await apiFetch("/api/admin-update-trainer-email", {
        method: "POST",
        body: JSON.stringify({ trenerId, newEmail: newEmail.trim().toLowerCase() }),
      });
      const j = await r.json();
      if (!r.ok) { setErrorMsg(j.error || `HTTP ${r.status}`); setPhase("error"); return; }
      if (j.noop) { setErrorMsg(j.message || "Žádná změna."); setPhase("error"); return; }
      setPreview(j);
      setPhase("preview");
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  const doConfirm = async () => {
    setPhase("saving");
    try {
      const r = await apiFetch("/api/admin-update-trainer-email", {
        method: "POST",
        body: JSON.stringify({ trenerId, newEmail: newEmail.trim().toLowerCase(), confirm: "yes" }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) { setErrorMsg(j.error || "Neočekávaná chyba"); setPhase("error"); return; }
      setResult(j);
      setPhase("done");
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Změnit email trenéra</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded min-h-[44px] sm:min-h-0"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm text-slate-600">
            <div><b>Trenér:</b> {displayName}</div>
            <div><b>Současný email:</b> {currentEmail || <span className="italic text-slate-400">(žádný)</span>}</div>
          </div>

          {phase === "input" && (
            <>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nový email</span>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="petr.janata.fw40@gmail.com"
                  className="field mt-1 w-full"
                  autoFocus
                />
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <b>POZOR, co se NESTANE automaticky:</b>
                <ul className="list-disc ml-4 mt-1">
                  <li>Reservio Google Calendar: musíš ručně re-linkovat na nový gmail v Reservio nastavení trenéra.</li>
                  <li>Trenér se musí přihlásit do admin appce novým emailem (pokud používá).</li>
                </ul>
              </div>
            </>
          )}

          {phase === "preview" && preview && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1">
              <div className="font-bold text-slate-700">Plán změn ({preview.oldEmail || "—"} → {preview.newEmail}):</div>
              <div>• <b>PG trainers:</b> ✓ UPDATE</div>
              <div>• <b>PG uzivatele:</b> {preview.plan.pg_uzivatele.willReplace ? "✓ UPDATE" : `${preview.plan.pg_uzivatele.reason}`}</div>
            </div>
          )}

          {phase === "done" && result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900 space-y-1">
              <div className="font-bold">Email změněn z {result.oldEmail || "—"} na {result.newEmail}</div>
              <div className="text-xs">PG trainers: {result.changes.trainers ? "✓" : "—"}, uzivatele: {result.changes.uzivatele ? "✓" : "—"}</div>
              {result.changes.errors?.length > 0 && (
                <div className="text-xs text-amber-700 mt-1">Některé kroky selhaly: {result.changes.errors.map((e) => `${e.step}: ${e.error}`).join("; ")}</div>
              )}
            </div>
          )}

          {phase === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-900">
              <b>Chyba:</b> {errorMsg}
            </div>
          )}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          {phase === "input" && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zrušit</button>
              <button onClick={doDryRun} disabled={!isValid} className="btn btn-primary">Náhled změn</button>
            </>
          )}
          {phase === "preview" && (
            <>
              <button onClick={() => setPhase("input")} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100">Zpět</button>
              <button onClick={doConfirm} className="btn btn-primary">Potvrdit a změnit</button>
            </>
          )}
          {phase === "saving" && (
            <div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Pracuji…</div>
          )}
          {phase === "done" && (
            <button onClick={onSaved} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-700 min-h-[44px] sm:min-h-0">Zavřít a obnovit</button>
          )}
          {phase === "error" && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zavřít</button>
              <button onClick={() => setPhase("input")} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-700">Zkusit znovu</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Karta v detail view — barevné kolečko s ikonou, tučný titulek, ohraničení, hover efekt
function DetailCard({ icon: Icon, title, tip, accent = "slate", children, className = "" }) {
  const accentMap = {
    emerald: { bg: "bg-emerald-100", text: "text-emerald-700" },
    violet:  { bg: "bg-brand-100",  text: "text-brand-700"  },
    amber:   { bg: "bg-amber-100",   text: "text-amber-700"   },
    sky:     { bg: "bg-sky-100",     text: "text-sky-700"     },
    rose:    { bg: "bg-rose-100",    text: "text-rose-700"    },
    indigo:  { bg: "bg-plum-100",  text: "text-plum-700"  },
    slate:   { bg: "bg-slate-100",   text: "text-slate-700"   },
  };
  const a = accentMap[accent] || accentMap.slate;
  // 13.9.2026: hlavička karty v detailu je světlý pruh jako skupiny v přehledech,
  // ne kolečko s ikonou. Barva zůstává, jen se přesunula do ikony.
  return (
    <div className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${className}`}>
      <div className="flex items-center gap-2 bg-brand-light px-4 py-2">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${a.text}`} />
        <span className="min-w-0 text-xs font-bold uppercase tracking-wide text-brand-950">{title}</span>
        {tip && <InfoTip text={tip} />}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Segmentová dlaždice — dvě sloupce (např. 40 dní / YTD) se stejným tvarem
function MovementSegment({ label, added, ended, accent = "slate" }) {
  const net = (added || 0) - (ended || 0);
  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-200",
    slate: "bg-slate-50 border-slate-200",
  };
  return (
    <div className={`${colorMap[accent] || colorMap.slate} border rounded-xl p-2.5`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 text-center">{label}</div>
      <div className="grid grid-cols-3 gap-1 items-center">
        <div className="text-center">
          <div className="inline-flex items-center gap-0.5 text-sm font-bold tabular-nums text-emerald-700">
            <ArrowUp className="w-3 h-3" />+{added || 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">přibyli</div>
        </div>
        <div className="text-center">
          <div className="inline-flex items-center gap-0.5 text-sm font-bold tabular-nums text-red-700">
            <ArrowDown className="w-3 h-3" />−{ended || 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">skončili</div>
        </div>
        <div className="text-center">
          <div className={`text-sm font-extrabold tabular-nums ${net > 0 ? "text-emerald-700" : net < 0 ? "text-red-700" : "text-slate-400"}`}>
            {net >= 0 ? "+" : ""}{net}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">net</div>
        </div>
      </div>
    </div>
  );
}

// Rozbalitelný řádek kategorie trialů (Výhra / Prohra / Čekají) s klikatelnými jmény klientů
function TrialCategoryRow({ label, count, names, color, active, onToggle }) {
  const canExpand = Array.isArray(names) && names.length > 0;
  return (
    <>
      <button
        type="button"
        onClick={canExpand ? onToggle : undefined}
        disabled={!canExpand}
        className={`w-full flex justify-between items-center ${canExpand ? "hover:bg-slate-50 rounded -mx-1 px-1 cursor-pointer" : "cursor-default"} transition-colors`}
      >
        <span className="text-slate-600 inline-flex items-center gap-1">
          {label}
          {canExpand && (
            active
              ? <ChevronDown className="w-3 h-3 text-slate-400" />
              : <ChevronRight className="w-3 h-3 text-slate-300" />
          )}
        </span>
        <span className={`font-bold tabular-nums ${color}`}>{count}</span>
      </button>
      {active && canExpand && (
        <div className="ml-4 pl-2 border-l-2 border-slate-200 space-y-0.5 pt-1 pb-1">
          {names.map((name, i) => (
            <div key={i} className="text-xs text-slate-700 truncate demo-sensitive">
              <ClientLink name={name} className="text-slate-700 hover:text-brand-700">{name}</ClientLink>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Barevná logika metrik (sdílená s dashboardem)
function winColor(v) {
  if (v == null) return "text-slate-400";
  if (v >= 50) return "text-emerald-700";
  if (v >= 30) return "text-amber-700";
  return "text-red-700";
}
function capColor(v) {
  if (v >= 81) return "text-emerald-700";
  if (v >= 70) return "text-green-600";
  if (v >= 50) return "text-amber-600";
  return "text-red-600";
}

// 13.9.2026 (fáze 4f, rozhodnutí Č2): karta trenéra má tmavou hlavičku jako
// karty jinde v aplikaci. Jméno je nadpis, role, lokalita a měsíc spolupráce
// jsou v podtitulku, pod hlavičkou jsou čtyři hlavní metriky jako dlaždice.
// Dřív to byl bílý řádek se štítky a pruhem sedmi čísel, který se na telefonu
// lámal na čtyři patra.
function TrainerRow({ trainer, expanded, onToggle, onEditEmail, onEditPhoto }) {
  const t = trainer;
  // Dropdown u kategorií trialů — jedna expanded najednou ve dvou kartách (60d + Celá historie)
  const [expandedCategory, setExpandedCategory] = useState(null);
  const toggleCategory = (key) => setExpandedCategory((cur) => (cur === key ? null : key));
  // React state misto direct DOM manipulace — driv onError nastavoval e.target.style.display
  // primo na DOM, ale pri re-renderu (napr. expand toggle) React naparsoval JSX znova,
  // vytvoril novy img element, ten znovu nacetl ze Drive a pri 429 cyclic onError → fotka
  // permanentne pryc. State drzime na urovni rowu, fallback iniciály se vykresli az kdyz
  // skutecne load selhal (a zustane stabilni i pres expand toggle).
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = t.foto && !photoFailed;

  const mesic = t.sazbaInfo?.mesicSpoluprace ?? ordinalMesicSpoluprace(t.zacatek);
  const podtitulek = [
    t.role || null,
    t.lokalita || null,
    mesic ? `${mesic}. měsíc` : null,
    t.status === "Aktivní" ? null : "neaktivní",
  ].filter(Boolean).join(", ");

  // Kapacita bere stejný zdroj jako rozbalený detail (realCapacity z patternu),
  // aby sbalená karta a otevřený detail seděly.
  const rc = t.realCapacity;
  const kapacitaPct = rc?.utilizationPct ?? (t.capacityHours > 0 ? t.utilizationPct : null);
  const workable = rc?.workableHours ?? t.capacityHours ?? 0;
  const kapacitaPopis = rc
    ? `${Math.round(workable * (rc.utilizationPct ?? 0) / 100)} z ${workable} h`
    : (t.capacityHours > 0 ? `${t.usedHoursPerWeek} z ${t.capacityHours} h` : "bez rozvrhu");

  const klientu = t.status === "Neaktivní" && t.maxClients > 0 ? t.maxClients : t.activeClientsCount;
  const klientuPopis = t.status === "Neaktivní" && t.maxClients > 0 ? "max. klientů" : "klientů";
  const detiWord = t.detiCount === 1 ? "dítě" : (t.detiCount < 5 ? "děti" : "dětí");

  return (
    <div className="glass overflow-hidden rounded-xl">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-left text-white sm:px-5"
      >
        <span
          role={onEditPhoto ? "button" : undefined}
          tabIndex={onEditPhoto ? 0 : undefined}
          onClick={(e) => { if (onEditPhoto) { e.stopPropagation(); onEditPhoto(); } }}
          onKeyDown={(e) => { if (onEditPhoto && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); e.stopPropagation(); onEditPhoto(); } }}
          title={onEditPhoto ? "Klepnutím změníš fotku" : ""}
          className="group relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/15"
          style={{ cursor: onEditPhoto ? "pointer" : "default" }}
        >
          {showPhoto ? (
            <img
              key={t.foto}
              src={rewriteGoogleDrive(t.foto)}
              alt={t.jmeno}
              className="h-full w-full object-cover demo-photo"
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <span className="text-sm font-bold demo-photo">
              {t.jmeno.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
            </span>
          )}
          {onEditPhoto && (
            <span className="absolute inset-0 hidden items-center justify-center bg-black/50 sm:group-hover:flex">
              <Pencil className="h-4 w-4 text-white" />
            </span>
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-serif text-lg font-bold leading-tight demo-sensitive">{t.jmeno}</span>
          {podtitulek && <span className="block text-xs leading-snug text-white/70 demo-sensitive">{podtitulek}</span>}
        </span>

        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10">
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </span>
      </button>

      {/* Čtyři metriky, které chce člověk vidět bez rozklikávání. Zbytek je v detailu. */}
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4 sm:p-4">
        <div className="rounded-xl bg-brand-light px-3 py-2">
          <div className="text-xl font-bold tabular-nums text-brand-950">{klientu}</div>
          <div className="text-xs font-semibold text-brand-deep">{klientuPopis}</div>
          {(t.added40d > 0 || t.ended40d > 0 || t.detiCount > 0) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs">
              {t.added40d > 0 && <span className="font-bold text-emerald-700">+{t.added40d}</span>}
              {t.ended40d > 0 && <span className="font-bold text-rose-700">−{t.ended40d}</span>}
              {(t.added40d > 0 || t.ended40d > 0) && <span className="text-slate-500">za 40 dní</span>}
              {t.detiCount > 0 && <span className="font-semibold text-brand-deep">+{t.detiCount} {detiWord}</span>}
            </div>
          )}
        </div>
        <div className="rounded-xl bg-emerald-50 px-3 py-2">
          <div className="text-xl font-bold tabular-nums text-emerald-700">
            {t.status === "Neaktivní" && !t.mrr && t.maxMrr > 0 ? fmt(t.maxMrr) : fmt(t.mrr)}
          </div>
          <div className="text-xs font-semibold text-emerald-600">
            {t.status === "Neaktivní" && !t.mrr && t.maxMrr > 0 ? "max. MRR, Kč" : "MRR, Kč"}
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            {t.permicePotential > 0 ? `+ ${fmt(t.permicePotential)} z permic` : `${t.mrrClientsCount || 0} pravidelných`}
          </div>
        </div>
        <div className="rounded-xl bg-slate-100 px-3 py-2">
          <div className={`text-xl font-bold tabular-nums ${kapacitaPct != null ? capColor(kapacitaPct) : "text-slate-400"}`}>
            {kapacitaPct != null ? `${kapacitaPct} %` : "—"}
          </div>
          <div className="text-xs font-semibold text-slate-500">kapacita</div>
          <div className="mt-0.5 text-xs text-slate-500">{kapacitaPopis}</div>
        </div>
        <div className="rounded-xl bg-slate-100 px-3 py-2">
          <div className={`text-xl font-bold tabular-nums ${winColor(t.winRate60)}`}>
            {t.winRate60 != null ? `${t.winRate60} %` : "—"}
          </div>
          <div className="text-xs font-semibold text-slate-500">win za 60 dní</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {t.deals60} z {t.deals60 + t.losses60} rozhodnutých
          </div>
        </div>
      </div>

      {/* Detail — expand. Nový design: barevné kolečko ikony, lepší hierarchie, 40d + YTD v pohybu klientů. */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 bg-gradient-to-b from-slate-50/80 to-slate-50/30">
          {/* Row 1 — 3 sloupce: Pohyb klientů | Kapacita | Výkonnost 60 dní */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            {/* Pohyb klientů — 40 dní + YTD segmenty */}
            <DetailCard
              icon={Calendar}
              title="Pohyb klientů"
              accent="emerald"
              tip="Kolik klientů přibylo (Začátek spolupráce v okně) a skončilo (Konec spolupráce v okně) v daném období. 40 dní = běžící pokladník pro aktuální situaci, YTD = celkový pohyb od 1.1. Net = rozdíl (růst/pokles klientely)."
            >
              <div className="space-y-2.5">
                <MovementSegment label="Za 40 dní" added={t.added40d} ended={t.ended40d} accent="emerald" />
                <MovementSegment label={`YTD ${new Date().getFullYear()}`} added={t.addedYTD} ended={t.endedYTD} accent="slate" />
              </div>
            </DetailCard>

            {/* Kapacita a obsazenost — z REÁLNÉHO patternu rozvrhu (sloty po odečtení mimo, oběda, absencí) */}
            {(t.realCapacity || t.capacityHours > 0) ? (() => {
              const rc = t.realCapacity || null;
              const workable = rc?.workableHours ?? t.capacityHours ?? 0;
              const fixedH = rc?.fixedHours ?? 0;
              const roamingExpected = rc?.roamingExpected ?? 0;
              const volnoH = rc?.volnoHours ?? 0;
              const lunchH = rc?.lunchHours ?? 0;
              const freeForSales = rc?.freeForSales ?? 0;
              const pct = rc?.utilizationPct ?? t.utilizationPct ?? 0;
              const color = pct >= 81 ? "#059669" : pct >= 70 ? "#16A34A" : pct >= 50 ? "#D97706" : "#DC2626";
              const accent = pct >= 70 ? "emerald" : pct >= 50 ? "amber" : "rose";
              return (
                <DetailCard
                  icon={Activity}
                  title="Kapacita a obsazenost"
                  accent={accent}
                  tip={rc
                    ? "Skutečná kapacita z týdenního rozvrhu trenéra (sloty po odečtení mimo dnů, oběda a plánovaných absencí). Obsazenost počítá pevně obsazené sloty plus odhad spotřeby putujícími klienty. 81%+ zelená (optimální), 70-80% zelená (mírná rezerva), 50-69% oranžová (podvytížen), pod 50% červená (volná kapacita)."
                    : "Plánovaná kapacita trenéra (údaj Kapacita) vs skutečně odučeno podle rezervací. 81%+ zelená (optimální), 70-80% zelená (mírná rezerva), 50-69% oranžová (podvytížen), pod 50% červená (volná kapacita)."}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm text-slate-600">Obsazenost</span>
                    <span className="text-3xl font-extrabold tabular-nums" style={{ color }}>{pct}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
                  </div>
                  {rc ? (
                    <>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Max kapacita</div>
                          <div className="text-base font-extrabold tabular-nums text-slate-800 mt-0.5">{workable}</div>
                          <div className="text-[11px] text-slate-500">h/týden v rozvrhu</div>
                        </div>
                        <div className={`${freeForSales > 0 ? "bg-emerald-50" : "bg-slate-50"} rounded-md p-2 text-center`}>
                          <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Volné sloty pro sales</div>
                          <div className={`text-base font-extrabold tabular-nums mt-0.5 ${freeForSales > 0 ? "text-emerald-700" : "text-slate-400"}`}>{freeForSales}</div>
                          <div className="text-[11px] text-slate-500">h/týden po roamingu</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1.5 text-center">
                        <div className="bg-brand-50 rounded p-1.5">
                          <div className="text-[11px] text-brand-700 font-bold uppercase">Pevně</div>
                          <div className="text-sm font-extrabold tabular-nums text-brand-800">{fixedH}h</div>
                        </div>
                        <div className="bg-sky-50 rounded p-1.5">
                          <div className="text-[11px] text-sky-700 font-bold uppercase">Putující</div>
                          <div className="text-sm font-extrabold tabular-nums text-sky-800">~{roamingExpected}h</div>
                        </div>
                        <div className="bg-emerald-50 rounded p-1.5">
                          <div className="text-[11px] text-emerald-700 font-bold uppercase">Volné sloty</div>
                          <div className="text-sm font-extrabold tabular-nums text-emerald-800">{volnoH}h</div>
                        </div>
                        <div className="bg-amber-50 rounded p-1.5">
                          <div className="text-[11px] text-amber-700 font-bold uppercase">Oběd</div>
                          <div className="text-sm font-extrabold tabular-nums text-amber-800">{lunchH}h</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Využito</div>
                        <div className="text-lg font-extrabold tabular-nums mt-0.5" style={{ color }}>{t.usedHoursPerWeek || 0}</div>
                        <div className="text-[11px] text-slate-500">h/týden</div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Kapacita</div>
                        <div className="text-lg font-extrabold tabular-nums text-slate-800 mt-0.5">{t.capacityHours}</div>
                        <div className="text-[11px] text-slate-500">h/týden</div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Volná</div>
                        <div className="text-lg font-extrabold tabular-nums text-slate-400 mt-0.5">{Math.max(0, (t.capacityHours || 0) - (t.usedHoursPerWeek || 0))}</div>
                        <div className="text-[11px] text-slate-500">h/týden</div>
                      </div>
                    </div>
                  )}
                </DetailCard>
              );
            })() : (
              <DetailCard icon={Activity} title="Kapacita a obsazenost" accent="slate">
                <div className="text-xs text-slate-400 italic">Nejsou k dispozici data o kapacitě.</div>
              </DetailCard>
            )}

            {/* Výkonnost 60 dní */}
            <DetailCard
              icon={Target}
              title="Výkonnost 60 dní"
              accent="violet"
              tip="Trial = lead, jehož stage prošel aspoň Trial. Výhra = Deal/Uzavřeno. Prohra = Prohra. Čekají = stage stále Trial. Win rate = výhry / (výhry + prohry). 60d = datum vytvoření leadu v posledních 60 dnech. Klikni na kategorii pro zobrazení jmen klientů."
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-slate-600">Trialy celkem</span>
                <span className="text-2xl font-extrabold tabular-nums text-slate-800">{t.trials60}</span>
              </div>
              <div className="space-y-1.5 text-sm">
                <TrialCategoryRow label="↳ Výhra" count={t.deals60} names={t.deals60Names} color="text-emerald-700" active={expandedCategory === "deals60"} onToggle={() => toggleCategory("deals60")} />
                <TrialCategoryRow label="↳ Prohra" count={t.losses60} names={t.losses60Names} color="text-red-700" active={expandedCategory === "losses60"} onToggle={() => toggleCategory("losses60")} />
                {t.pending60 > 0 && (
                  <TrialCategoryRow label="↳ Čekají" count={t.pending60} names={t.pending60Names} color="text-amber-700" active={expandedCategory === "pending60"} onToggle={() => toggleCategory("pending60")} />
                )}
              </div>
              <div className="flex items-baseline justify-between mt-2.5 pt-2 border-t border-slate-100">
                <span className="text-sm text-slate-600 font-semibold">Win rate</span>
                <span className={`text-xl font-extrabold tabular-nums ${t.winRate60 == null ? "text-slate-400" : t.winRate60 >= 50 ? "text-emerald-700" : t.winRate60 >= 30 ? "text-amber-700" : "text-red-700"}`}>
                  {t.winRate60 != null ? `${t.winRate60}%` : "—"}
                </span>
              </div>
            </DetailCard>
          </div>

          {/* Row 2 — 3 sloupce: Ekonomika | Celá historie | Balíčky */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            {/* Ekonomika (MRR + permice potenciál + konverze + sazba/odměna) */}
            <DetailCard
              icon={DollarSign}
              title="Ekonomika"
              accent="emerald"
              tip="MRR pravidelné = součet Částka/měs u pravidelných klientů (frekvence Měsíčně/Čtvrtletně, konec > 30d, NEpermice). Revenue z permic = počet absolvovaných tréninků za posledních 30 dní × cena za trénink z permice (Částka/měs ÷ Počet tréninků v balíčku). Pokud u klienta chybí Počet tréninků, fallback na heuristiku 40 % × Částka. Lifetime = obrat klientů trenéra přes všechny 3 FAPI účty, jen za období, kdy byl klient přiřazený tomuto trenérovi (převzaté klienty nepočítáme z doby u předchozího trenéra)."
            >
              {/* Hlavní MRR blok */}
              <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-3 mb-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-600 font-medium">MRR pravidelné</span>
                  <span className="text-2xl font-extrabold tabular-nums text-emerald-700">{fmt(t.mrr)} Kč</span>
                </div>
                <div className="text-[11px] text-slate-500 text-right mt-0.5">{t.mrrClientsCount} pravidelných, Ø {fmt(t.avgClientMrr)} Kč</div>
                {t.permicePotential > 0 && (
                  <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-emerald-100">
                    <span className="text-sm text-brand-700 inline-flex items-center gap-0.5">+ Potenciál z permic</span>
                    <span className="text-base font-bold tabular-nums text-brand-600">+{fmt(t.permicePotential)} Kč</span>
                  </div>
                )}
                {t.permicePotential > 0 && (
                  <div className="text-[11px] text-brand-500 text-right mt-0.5">40 % × {t.permiceClientsCount} aktivních permic</div>
                )}
              </div>

              {/* Lifetime revenue + konverze + sazba */}
              <div className="space-y-1.5 text-sm">
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-600 inline-flex items-center gap-0.5"><Award className="w-3.5 h-3.5 text-amber-500" /> Lifetime revenue</span>
                  <span className="font-bold tabular-nums text-amber-700">{fmt(t.lifetimeRevenue)} Kč</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-600 inline-flex items-center gap-0.5"><Percent className="w-3.5 h-3.5 text-slate-400" /> Konverze z trialu <InfoTip text={`${t.trialClientsCount} z ${t.totalClientsCount} klientů (aktivních + ukončených) tohoto trenéra mělo v FAPI trial fakturu. Tj. % klientů přišlých skrze trial.`}/></span>
                  <span className={`font-bold tabular-nums ${t.trialConversion >= 50 ? "text-emerald-700" : t.trialConversion >= 25 ? "text-amber-700" : "text-slate-700"}`}>
                    {t.trialConversion}% <span className="font-normal text-[11px] text-slate-500">({t.trialClientsCount}/{t.totalClientsCount})</span>
                  </span>
                </div>
                {t.sazba > 0 && (
                  <SazbaRow trener={t} />
                )}
                {(t.wagesMesic > 0 || t.wagesYTD > 0) && (
                  <WagesRows trener={t} />
                )}
                {t.kapacita > 0 && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-slate-600 inline-flex items-center gap-0.5">Plán. kapacita <InfoTip text="Kolik hodin měsíčně trenér chce/může odučit (údaj Kapacita u trenéra v databázi). Pomáhá plánovat obsazenost." /></span>
                    <span className="font-bold tabular-nums text-slate-800">{fmt(t.kapacita)} h/měs</span>
                  </div>
                )}
                {t.odmena > 0 && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-slate-600">Měsíční odměna</span>
                    <span className="font-bold tabular-nums text-slate-800">{fmt(t.odmena)} Kč</span>
                  </div>
                )}
              </div>
            </DetailCard>

            {/* Celá historie */}
            <DetailCard
              icon={Award}
              title="Celá historie"
              accent="sky"
              tip="Trialy bez časového omezení. Pro období před únorem 2026 (před Reservio integrací) jsou k dispozici jen ty trialy, co jsou v Notion/Leady zaznamenané. Klikni na kategorii pro zobrazení jmen klientů, z jmen proklik na profil."
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-slate-600">Trialy celkem</span>
                <span className="text-2xl font-extrabold tabular-nums text-slate-800">{t.trialsAll}</span>
              </div>
              <div className="space-y-1.5 text-sm">
                <TrialCategoryRow label="↳ Výhra" count={t.dealsAll} names={t.dealsAllNames} color="text-emerald-700" active={expandedCategory === "dealsAll"} onToggle={() => toggleCategory("dealsAll")} />
                <TrialCategoryRow label="↳ Prohra" count={t.lossesAll} names={t.lossesAllNames} color="text-red-700" active={expandedCategory === "lossesAll"} onToggle={() => toggleCategory("lossesAll")} />
                {t.pendingAll > 0 && (
                  <TrialCategoryRow label="↳ Čekají" count={t.pendingAll} names={t.pendingAllNames} color="text-amber-700" active={expandedCategory === "pendingAll"} onToggle={() => toggleCategory("pendingAll")} />
                )}
              </div>
              <div className="flex items-baseline justify-between mt-2.5 pt-2 border-t border-slate-100">
                <span className="text-sm text-slate-600 font-semibold">Win rate celkem</span>
                <span className={`text-xl font-extrabold tabular-nums ${t.winRateAll == null ? "text-slate-400" : t.winRateAll >= 50 ? "text-emerald-700" : t.winRateAll >= 30 ? "text-amber-700" : "text-red-700"}`}>
                  {t.winRateAll != null ? `${t.winRateAll}%` : "—"}
                </span>
              </div>
              {t.avgCoopMonths > 0 && (
                <div className="flex items-baseline justify-between mt-1.5 text-sm">
                  <span className="text-slate-600 inline-flex items-center gap-0.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> Ø délka spolupráce <InfoTip text="Podle počtu placených FAPI faktur. Permanentka = 2 měsíce, Koučink = 1 měsíc. Zahrnuje aktivní i ukončené klienty. Jen faktury z období přiřazení k tomuto trenérovi (nejdřív od jeho nástupu)." /></span>
                  <span className="font-bold tabular-nums text-slate-800">{t.avgCoopMonths} měs</span>
                </div>
              )}
            </DetailCard>

            {/* Balíčky aktivních klientů */}
            <DetailCard
              icon={Package}
              title="Balíčky aktivních klientů"
              accent="indigo"
              tip={t.topPackage ? `Top balíček: ${t.topPackage}` : undefined}
            >
              {Object.keys(t.packageDistribution || {}).length === 0 ? (
                <div className="text-xs text-slate-400 italic">Žádná data</div>
              ) : (() => {
                const entries = Object.entries(t.packageDistribution || {}).sort((a, b) => b[1] - a[1]);
                const total = entries.reduce((s, [, c]) => s + c, 0);
                return (
                  <div className="space-y-2">
                    {entries.map(([pkg, cnt]) => {
                      const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
                      return (
                        <div key={pkg}>
                          <div className="flex justify-between text-sm mb-0.5">
                            <span className="text-slate-700 truncate flex-1 demo-sensitive">{pkg}</span>
                            <span className="font-bold tabular-nums text-slate-800 ml-2 shrink-0">{cnt}<span className="text-[11px] text-slate-500 font-normal">×, {pct} %</span></span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-plum-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </DetailCard>
          </div>

          {/* Seznam aktivních klientů — full width row 3 */}
          {t.activeClients.length > 0 && (
            <div className="mt-4">
              <DetailCard
                icon={Users}
                title={<span>Aktivní klienti <span className="text-slate-400 font-normal ml-1">({t.activeClients.length})</span></span>}
                accent="slate"
              >
                <div className="divide-y divide-slate-100 -mx-4 -my-4 max-h-80 overflow-y-auto">
                  {t.activeClients.map((c, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-slate-50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate demo-sensitive">
                          <ClientLink name={c.name} className="text-slate-800 hover:text-brand-700">{c.name}</ClientLink>
                        </div>
                        <div className="text-xs text-slate-500 truncate demo-sensitive">{c.balicek}{c.zacatek && `, od ${c.zacatek}`}</div>
                      </div>
                      {c.mrr > 0 && <div className="font-bold tabular-nums text-emerald-700 text-xs whitespace-nowrap">{fmt(c.mrr)} Kč/měs</div>}
                    </div>
                  ))}
                </div>
              </DetailCard>
            </div>
          )}

          {(!t.sazba || !t.odmena) && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-800 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Pro výpočet zisku per trenér doplň u trenéra v HR, Lidé:
                {!t.sazba && <> <b>Začátek spolupráce</b> a <b>Režim výpočtu sazby</b> (pro sazbu)</>}
                {!t.sazba && !t.odmena && " +"}
                {!t.odmena && <> <b>Fixní odměna měsíčně</b></>}
                .
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// === SAZBA ROW — zobrazení sazby s rozpadem na základ + bonus + příští milník ===
// Používá data z trener.sazbaInfo (vypočtena na backendu z Přílohy A.2 smlouvy)
function SazbaRow({ trener: t }) {
  const info = t.sazbaInfo || {};
  const isVyjimka = info.rezim === "Vyjimka";
  const milnik = info.pristiMilnik;

  // Vysvětlení pro tooltip
  const tooltipText = isVyjimka
    ? `Smluvní výjimka mimo standardní vzorec. Sazba je nastavena pevně na ${t.sazba} Kč/jednotka bez ohledu na délku spolupráce.`
    : info.source === "smlouva"
      ? `Aktuální sazba dle smlouvy (Příloha A.2). Trenér je ${info.mesicSpoluprace}. měsíc spolupráce. Základ ${info.zaklad} Kč + motivační bonus ${info.bonus} Kč. Bonus se vyplácí pouze pokud trenér v měsíci splnil podmínky kvality (max 2 porušení).`
      : info.varovani || "Sazba zatím není automaticky spočítaná. Doplň u trenéra v HR, Lidé Začátek spolupráce a Režim výpočtu sazby.";

  return (
    <div className="space-y-1">
      {/* Hlavní řádek se sazbou */}
      <div className="flex items-baseline justify-between">
        <span className="text-slate-600 inline-flex items-center gap-1">
          Sazba/jednotka
          {isVyjimka && <span className="text-[11px] uppercase tracking-wider bg-brand-100 text-brand-700 font-bold px-1.5 py-0.5 rounded">Výjimka</span>}
          <InfoTip text={tooltipText} />
        </span>
        <span className="font-bold tabular-nums text-slate-800 demo-photo-target">{fmt(t.sazba)} Kč</span>
      </div>

      {/* Rozpad základ + bonus (jen pro smluvní režim) */}
      {!isVyjimka && info.bonus > 0 && (
        <div className="flex items-baseline justify-between text-[11px] text-slate-500 -mt-0.5">
          <span className="pl-2">└ základ {info.zaklad} Kč + bonus {info.bonus} Kč</span>
          {info.mesicSpoluprace != null && (
            <span className="text-[11px]">{info.mesicSpoluprace}. měsíc</span>
          )}
        </div>
      )}

      {/* Příští milník — kdy se sazba zvedne */}
      {milnik && (
        <div className="flex items-baseline justify-between text-[11px] bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mt-1">
          <span className="text-emerald-700 inline-flex items-center gap-1">
            <span>↗</span> Příští navýšení {fmtCzDate(milnik.datum)}
          </span>
          <span className="font-bold tabular-nums text-emerald-800">
            {milnik.sazba} Kč <span className="text-[11px] font-normal opacity-80">(+{milnik.naroustOKc})</span>
          </span>
        </div>
      )}

      {/* Varování pokud není automatický výpočet */}
      {info.varovani && !isVyjimka && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1 mt-1">
          {info.varovani}
        </div>
      )}
    </div>
  );
}

// Helper pro hezký český datum (DD. MM. YYYY)
function fmtCzDate(iso) {
  if (!iso) return "—";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}`;
}

// České názvy měsíců (1-indexed)
const MESICE_GENITIV = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

// Vytvoří label pro Náklady YTD KPI dlaždici z firstClosedMonth + lastClosedMonth.
// Pokud je jeden měsíc → "Náklady leden 2026"
// Pokud je víc → "Náklady leden–březen 2026"
// Pokud žádný uzavřený → "Náklady letos (žádný uzavřený měsíc)"
function wagesYTDLabel(summary) {
  const first = summary?.firstClosedMonth; // YYYY-MM
  const last = summary?.lastClosedMonth;
  if (!first || !last) return "Náklady letos";
  const [fy, fm] = first.split("-").map(Number);
  const [ly, lm] = last.split("-").map(Number);
  const firstName = MESICE_GENITIV[fm - 1];
  const lastName = MESICE_GENITIV[lm - 1];
  if (first === last) return `Náklady ${firstName} ${fy}`;
  if (fy === ly) return `Náklady ${firstName}–${lastName} ${fy}`;
  return `Náklady ${firstName} ${fy} – ${lastName} ${ly}`;
}

// === WAGES ROWS — výplata trenéra (tento měsíc + YTD respektující tier escalation) ===
function WagesRows({ trener: t }) {
  const wagesMesicTip = `Sazba ${t.sazba} Kč × ${t.treninkyMesic} odtréninků v aktuálním měsíci. Bonus zahrnut. Pro storno < 24 h klientem náleží plná sazba (smlouva čl. 3.3).`;
  const wagesYTDTip = `Kumulativní výplata od 1.1.${new Date().getFullYear()}. Pro každý měsíc se počítá příslušná sazba (poslední den měsíce), takže respektuje navýšení v průběhu roku. Celkem ${t.treninkyYTD} odtréninků.`;

  return (
    <>
      {/* Vyděláno tento měsíc (probíhá) */}
      <div className="flex items-baseline justify-between">
        <span className="text-slate-600 inline-flex items-center gap-1">
          <DollarSign className="w-3.5 h-3.5 text-slate-400" />
          Vyděláno tento měsíc
          <InfoTip text={wagesMesicTip} />
        </span>
        <span className="font-bold tabular-nums text-emerald-700 demo-photo-target">
          {fmt(t.wagesMesic || 0)} Kč
          <span className="text-[11px] font-normal text-slate-500 ml-1">({t.treninkyMesic} {t.treninkyMesic === 1 ? "trénink" : t.treninkyMesic < 5 ? "tréninky" : "tréninků"})</span>
        </span>
      </div>

      {/* YTD vyděláno (vč aktuálního měsíce) */}
      {t.wagesYTD > 0 && (
        <div className="flex items-baseline justify-between">
          <span className="text-slate-600 inline-flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
            Vyděláno YTD
            <InfoTip text={wagesYTDTip} />
          </span>
          <span className="font-bold tabular-nums text-slate-800 demo-photo-target">
            {fmt(t.wagesYTD)} Kč
            <span className="text-[11px] font-normal text-slate-500 ml-1">({t.treninkyYTD} {t.treninkyYTD === 1 ? "trénink" : t.treninkyYTD < 5 ? "tréninky" : "tréninků"})</span>
          </span>
        </div>
      )}
    </>
  );
}
